
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import { seoInfo } from '@/common/mixins/seoInfo';
import LazyHydrate from 'vue-lazy-hydration';
import TopPart from '~/components/homepage/TopPart';
import Features from '~/components/homepage/Features';
import Search from '~/components/search/Search';
import cacheControl from '~/common/helpers/cacheControl';

export default {
  middleware: cacheControl({
    'max-age': 60,
    'stale-when-revalidate': 5,
  }),
  components: {
    LazyHydrate,
    Search,
    Features,
    TopPart,
    Icon: () => import('~/common/shared-components/Icon.vue'),
    Breadcrumb: () => import('~/common/shared-components/Breadcrumb'),
    GoogleMap: () => import('~/common/shared-components/GoogleMap'),
    Subscription: () => import('~/components/homepage/Subscription'),
  },
  mixins: [seoInfo],
  async fetch () {
    try {
      await this.fetchAllHotelsByCityCode(this.$route.params.cityName);
    } catch (error) {
      this.$nuxt.context.redirect(this.localeLocation('/'));
      return;
    }

    const formattedHotelName = this.$route.params.hotelName.replace(/-/g, ' ');
    const hotel =
      this.hotels.find(
        hotel =>
          hotel.name
            .replace(/-/g, ' ')
            .replace(/[,.&@'+:’]/g, '')
            .replace(/ô/, 'o')
            .replace(/é/, 'e')
            .replace(/â/, 'a')
            .toLowerCase() === formattedHotelName
      ) || null;

    if (!hotel) {
      this.$nuxt.context.redirect(this.localeLocation('/'));
      return;
    }

    this.setCurrentHotelProduct(hotel);
    this.setSearch({
      cityCodeDestination: this.$route.params.cityName,
      dateStart: moment(new Date()).add(1, 'days').format('YYYY-MM-DD'),
      dateEnd: moment(new Date()).add(4, 'days').format('YYYY-MM-DD'),
    });
  },
  computed: {
    ...mapGetters({
      hotels: 'hotel/getHotelsByCityCode',
      hotelDestinations: 'destination/getHotelDestinations',
      currentHotelProduct: 'hotel/getCurrentHotelProduct',
      agents: 'destination/getAgents',
    }),
    destination () {
      return this.hotelDestinations.find(
        dest => dest.code === this.$route.params.cityName
      );
    },
    lang () {
      return this.$i18n.locale;
    },
  },
  methods: {
    ...mapActions({
      fetchAgents: 'destination/fetchAgents',
      fetchAllHotelsByCityCode: 'hotel/fetchAllHotelsByCityCode',
      setCurrentHotelProduct: 'hotel/setCurrentHotelProduct',
      setSearch: 'search/setSearch',
    }),
    fetchRequiredData () {
      this.fetchAgents(this.$route.params.cityName);
    },
  },
  head () {
    return {
      title: this.getSEOHotelTitle(
        this.currentHotelProduct.city,
        this.currentHotelProduct.name,
        this.lang
      ),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.getSEOHotelDescription(
            this.currentHotelProduct.city,
            this.currentHotelProduct.name,
            this.lang
          ),
        },
        {
          hid: 'og:title',
          property: 'og:title',
          content: this.getSEOHotelTitle(
            this.currentHotelProduct.city,
            this.currentHotelProduct.name,
            this.lang
          ),
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: this.getSEOHotelDescription(
            this.currentHotelProduct.city,
            this.currentHotelProduct.name,
            this.lang
          ),
        },
        {
          hid: 'og:url',
          property: 'og:url',
          content: `https://www.tripeze.com${this.$route.path}`,
        },
        {
          hid: 'og:type',
          property: 'og:type',
          content: 'website',
        },
        {
          hid: 'og:site_name',
          property: 'og:site_name',
          content: 'Tripeze',
        },
        {
          property: 'og:image',
          content:
            'https://www.tripeze.com/_ipx/s_112x112/images/logos/tripeze-logo.png',
        },
        {
          name: 'og:image:alt',
          content: this.getTwitterImageAlt(
            this.currentHotelProduct.city,
            '',
            this.lang
          ),
        },
        {
          name: 'twitter:image',
          content:
            'https://www.tripeze.com/_ipx/s_112x112/images/logos/tripeze-logo.png',
        },
        {
          name: 'twitter:site',
          content: '@GoTripeze',
        },
        {
          name: 'twitter:creator',
          content: '@GoTripeze',
        },
        {
          'data-react-helmet': true,
          name: 'twitter:card',
          content: 'summary',
        },
        {
          hid: 'twitter:title',
          name: 'twitter:title',
          content: this.getSEOHotelTitle(
            this.currentHotelProduct.city,
            this.currentHotelProduct.name,
            this.lang
          ),
        },
        {
          hid: 'twitter:description',
          name: 'twitter:description',
          content: this.getSEOHotelDescription(
            this.currentHotelProduct.city,
            this.currentHotelProduct.name,
            this.lang
          ),
        },
        {
          hid: 'twitter:image:alt',
          name: 'twitter:image:alt',
          content: this.getTwitterImageAlt(
            this.currentHotelProduct.city,
            '',
            this.lang
          ),
        },
      ],
      link: [
        {
          rel: 'canonical',
          href: `https://www.tripeze.com${this.$route.path}`,
        },
      ],
      script: [
        {
          innerHTML: `
          {
            "@context": "https://schema.org",
            "@type": "TravelAction",
            "toLocation": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "${this.currentHotelProduct.city}"
              }
            }
          }`,
          type: 'application/ld+json',
        },
      ],
    };
  },
};
